/**
 * attribution/capture.ts — client-side marketing attribution
 *
 * Captures UTM params, click IDs (gclid/fbclid/etc.), referrer, and
 * landing page on first visit; persists to a first-party cookie
 * `bizrnr_attrib` (90-day) so any downstream signup/checkout can attach
 * source data to the DB row.
 *
 * Two touches are tracked:
 *   - first_touch: written once, never overwritten (first paid-ad click wins).
 *   - last_touch: overwritten whenever the user returns with any attribution
 *     signal (UTM or click ID). If the user comes back with no params,
 *     last_touch is preserved.
 */

export const ATTRIB_COOKIE = 'bizrnr_attrib';
export const SESSION_COOKIE = 'bizrnr_session';
export const ATTRIB_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days

export interface AttributionTouch {
  source: string | null;        // utm_source
  medium: string | null;        // utm_medium
  campaign: string | null;      // utm_campaign
  content: string | null;       // utm_content
  term: string | null;          // utm_term
  gclid: string | null;         // Google Ads click ID
  gbraid: string | null;        // Google Ads iOS/app
  wbraid: string | null;        // Google Ads web
  fbclid: string | null;        // Meta click ID
  ttclid: string | null;        // TikTok click ID
  li_fat_id: string | null;     // LinkedIn click ID
  msclkid: string | null;       // Microsoft/Bing click ID
  referrer: string | null;
  landing_page: string | null;
  at: string;                   // ISO timestamp
}

export interface AttributionSnapshot {
  first_touch: AttributionTouch | null;
  last_touch: AttributionTouch | null;
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'li_fat_id', 'msclkid'] as const;

/** Parse a URL search string into a partial attribution touch. Null unless at least one attribution signal present. */
function parseTouchFromSearch(search: string, referrer: string, landingPath: string): AttributionTouch | null {
  const params = new URLSearchParams(search);
  const touch: Partial<AttributionTouch> = {};
  let hasSignal = false;

  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) hasSignal = true;
    touch[key.replace('utm_', '') as keyof AttributionTouch] = v as never;
  }
  for (const key of CLICK_ID_KEYS) {
    const v = params.get(key);
    if (v) hasSignal = true;
    touch[key as keyof AttributionTouch] = v as never;
  }

  // Fall back: if no UTM/click-ID but referrer is off-site, still record last_touch
  // so we can attribute organic referrals.
  const hasExternalReferrer = Boolean(referrer) && !referrer.startsWith(window.location.origin);
  if (!hasSignal && !hasExternalReferrer) return null;

  touch.source ??= null;
  touch.medium ??= null;
  touch.campaign ??= null;
  touch.content ??= null;
  touch.term ??= null;
  touch.gclid ??= null;
  touch.gbraid ??= null;
  touch.wbraid ??= null;
  touch.fbclid ??= null;
  touch.ttclid ??= null;
  touch.li_fat_id ??= null;
  touch.msclkid ??= null;
  touch.referrer = referrer || null;
  touch.landing_page = landingPath || null;
  touch.at = new Date().toISOString();

  return touch as AttributionTouch;
}

/** Read cookie by name. Returns raw value or null. */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((c) => c.startsWith(name + '='));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

/** Write cookie. Always SameSite=Lax, Secure in prod.
 *  Sets Domain=.bizrnr.com so cookies persist across subdomains
 *  (e.g. staging.bizrnr.com → bizrnr.com).
 */
function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  // Set domain to root so cookies persist across subdomains (staging.bizrnr.com → bizrnr.com)
  const hostname = window.location.hostname;
  const domainAttr = hostname.includes('bizrnr.com') ? '; Domain=.bizrnr.com' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}${domainAttr}`;
}

/** Load attribution snapshot from cookie + localStorage. Cookie wins. */
export function loadAttribution(): AttributionSnapshot {
  const empty: AttributionSnapshot = { first_touch: null, last_touch: null };
  if (typeof window === 'undefined') return empty;

  const raw = readCookie(ATTRIB_COOKIE) || (typeof localStorage !== 'undefined' ? localStorage.getItem(ATTRIB_COOKIE) : null);
  if (!raw) return empty;
  try {
    const parsed = JSON.parse(raw) as AttributionSnapshot;
    if (parsed && typeof parsed === 'object') {
      return {
        first_touch: parsed.first_touch ?? null,
        last_touch: parsed.last_touch ?? null,
      };
    }
  } catch {
    /* corrupted cookie — ignore */
  }
  return empty;
}

/** Persist snapshot to cookie + localStorage. */
function persistAttribution(snap: AttributionSnapshot): void {
  const json = JSON.stringify(snap);
  writeCookie(ATTRIB_COOKIE, json, ATTRIB_COOKIE_MAX_AGE_SECONDS);
  try {
    localStorage.setItem(ATTRIB_COOKIE, json);
  } catch {
    /* storage quota / blocked — cookie still written */
  }
}

/** True when a touch carries any paid-ad click ID (gclid, fbclid, …). */
function hasClickId(touch: AttributionTouch): boolean {
  return Boolean(
    touch.gclid || touch.gbraid || touch.wbraid || touch.fbclid ||
    touch.ttclid || touch.li_fat_id || touch.msclkid,
  );
}

/**
 * Detect an "internal re-tag": a same-origin navigation whose only attribution
 * signal is UTM params (no ad click ID). These come from on-site links that
 * still carry `?utm_source=…` (e.g. answer-page related links). Google's own
 * guidance is that internal links must not be campaign-tagged because they
 * reset the session source — here we neutralize that at the attribution layer
 * so it holds for any current or future internal link without a maintained
 * allow-list. An ad click (gclid/…) or an off-site referrer is never internal.
 */
export function isInternalReTag(touch: AttributionTouch, referrer: string, origin: string): boolean {
  if (hasClickId(touch)) return false;
  return Boolean(referrer) && Boolean(origin) && referrer.startsWith(origin);
}

/**
 * Pure attribution-merge decision (no DOM, no side effects → unit-testable).
 *
 * - No new touch → unchanged.
 * - First real touch always wins for `first_touch` (write-once).
 * - An internal re-tag (see {@link isInternalReTag}) does NOT overwrite an
 *   already-captured touch — otherwise a paid `gclid` landing is silently
 *   relabelled the instant the visitor clicks an on-site `?utm_source=` link.
 * - Genuine new external entries (ad click ID, or an off-site referrer) still
 *   update `last_touch`.
 */
export function nextAttribution(
  current: AttributionSnapshot,
  touch: AttributionTouch | null,
  referrer: string,
  origin: string,
): { snapshot: AttributionSnapshot; changed: boolean } {
  if (!touch) return { snapshot: current, changed: false };

  const hasPrior = Boolean(current.first_touch || current.last_touch);
  if (hasPrior && isInternalReTag(touch, referrer, origin)) {
    return { snapshot: current, changed: false };
  }

  return {
    snapshot: { first_touch: current.first_touch ?? touch, last_touch: touch },
    changed: true,
  };
}

/**
 * Run on every page load (idempotent):
 *  - If URL has UTM / click-ID / external referrer, update last_touch — UNLESS
 *    it is an internal re-tag over existing attribution (see nextAttribution).
 *  - If first_touch is empty, initialize it with the same values (write-once).
 */
export function captureAttribution(): AttributionSnapshot {
  if (typeof window === 'undefined') return { first_touch: null, last_touch: null };

  const search = window.location.search || '';
  const referrer = document.referrer || '';
  const landingPath = window.location.pathname + (search || '');

  const current = loadAttribution();
  const touch = parseTouchFromSearch(search, referrer, landingPath);

  const { snapshot, changed } = nextAttribution(
    current,
    touch,
    referrer,
    window.location.origin,
  );
  let snap = snapshot;
  if (changed) persistAttribution(snap);

  // Fallback: if still no attribution at all (direct visitor, no UTMs,
  // no external referrer), write a minimal "(direct)" entry so the
  // signup form and server-side fallback always find a cookie.
  // Only write once — don't overwrite existing attribution.
  if (!snap.first_touch && !snap.last_touch) {
    const directTouch: AttributionTouch = {
      source: '(direct)',
      medium: '(none)',
      campaign: null,
      content: null,
      term: null,
      gclid: null,
      gbraid: null,
      wbraid: null,
      fbclid: null,
      ttclid: null,
      li_fat_id: null,
      msclkid: null,
      referrer: null,
      landing_page: window.location.pathname + window.location.search,
      at: new Date().toISOString(),
    };
    snap = { first_touch: directTouch, last_touch: directTouch };
    persistAttribution(snap);
  }

  return snap;
}

/**
 * Flatten the snapshot into event properties suitable for PostHog / GTM / server payloads.
 * Uses `ft_*` and `lt_*` prefixes so downstream SQL / columns can be named consistently.
 */
export function flattenAttribution(snap: AttributionSnapshot): Record<string, string | null> {
  const flat: Record<string, string | null> = {};
  const stamp = (prefix: 'ft' | 'lt', t: AttributionTouch | null) => {
    const keys: (keyof AttributionTouch)[] = [
      'source', 'medium', 'campaign', 'content', 'term',
      'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'li_fat_id', 'msclkid',
      'referrer', 'landing_page', 'at',
    ];
    for (const k of keys) {
      flat[`${prefix}_${k}`] = t ? (t[k] ?? null) : null;
    }
  };
  stamp('ft', snap.first_touch);
  stamp('lt', snap.last_touch);
  return flat;
}

/** Convenience: load + flatten in one call. Safe to invoke from any client handler. */
export function getAttribution(): Record<string, string | null> {
  return flattenAttribution(loadAttribution());
}

/**
 * Read or mint a persistent anonymous session ID (90-day cookie).
 * Used by /api/track/touch to tie anonymous visits to the same visitor.
 */
export function getSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  let id = readCookie(SESSION_COOKIE);
  if (id && /^[a-zA-Z0-9_-]{8,64}$/.test(id)) return id;

  // Mint a new one. crypto.randomUUID is universal in modern browsers; fall
  // back to a random string for ancient runtimes.
  id = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  writeCookie(SESSION_COOKIE, id, ATTRIB_COOKIE_MAX_AGE_SECONDS);
  return id;
}

/**
 * Fire a touchpoint POST to /api/track/touch. Runs once per page load from
 * AnalyticsProvider. Fail-silent — never blocks rendering.
 */
export async function recordTouchpoint(): Promise<void> {
  if (typeof window === 'undefined') return;
  const sessionId = getSessionId();
  if (!sessionId) return;

  const snap = loadAttribution();
  const lt = snap.last_touch;
  const payload = {
    session_id: sessionId,
    source: lt?.source ?? null,
    medium: lt?.medium ?? null,
    campaign: lt?.campaign ?? null,
    content: lt?.content ?? null,
    term: lt?.term ?? null,
    gclid: lt?.gclid ?? null,
    gbraid: lt?.gbraid ?? null,
    wbraid: lt?.wbraid ?? null,
    fbclid: lt?.fbclid ?? null,
    ttclid: lt?.ttclid ?? null,
    li_fat_id: lt?.li_fat_id ?? null,
    msclkid: lt?.msclkid ?? null,
    referrer: document.referrer || null,
    landing_page: lt?.landing_page ?? null,
    path: window.location.pathname + window.location.search,
  };
  try {
    // Use Beacon when available so navigation away doesn't cancel the POST.
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const sent = navigator.sendBeacon('/api/track/touch', blob);
      if (sent) return;
      // sendBeacon returned false (queue full / page unloading) — fall through to fetch
    }
    await fetch('/api/track/touch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch { /* fail-silent */ }
}
